/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './ButtonDropdownChild';

const ButtonDropdownChildManager: ComponentManagerFn<'ButtonDropdownChild'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ButtonDropdownChild'>(
      platform,
      'ButtonDropdownChild',
    ),
    type: 'ButtonDropdownChild',
    name: 'Dropdown Item',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      text: {
        label: 'Text',
        type: 'expression',
        returnType: async () => 'string',
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.text.value,
    category: 'Toolbar',
    icon: { iconName: 'list-alt' },
    description: `**Overview**<br/>
The Dropdown Item is a specific sub-component used exclusively within a Button Dropdown. It represents a single actionable item (a row) inside the dropdown menu that appears when the main button is clicked.
Use this component to define the individual options, commands, or links available in your dropdown menu.

**1. Structure**<br/>
* **Parent Requirement:** This component is designed to exist only as a child of ButtonDropdown.
* **Trigger Logic:** Like a standard button, it supports a TriggerOnClick child component to define the action that occurs when this specific menu item is selected (e.g., navigating to a page or running a script).

**2. Configuration**<br/>
* **Text:** The label displayed for the menu item (e.g., "Print Invoice"). This supports expressions for dynamic text.
* **Icon:** An optional icon displayed to the left of the text, helping users quickly scan the menu (e.g., a "Printer" icon).
* **Disable Condition:** An expression that can conditionally disable this specific item without affecting the rest of the menu (e.g., disabling "Delete" if the user doesn't have permission).

**3. Best Use Cases**<br/>
* **Sub-Actions:** "Download PDF" and "Download CSV" as children of an "Export" dropdown.
* **Status Toggles:** "Mark as Read" and "Mark as Unread" options.
* **Navigation:** Deep links to specific sections like "My Profile," "Settings," and "Logout" inside a user menu.`,
  };
};

export default ButtonDropdownChildManager;
